import {defaultBackgroundColor} from '../constants-defaults'
import {Grid} from '../grid/grid'
import {getColor, hex2dec, RGBColor, setColor} from '../util/color'
import {Renderer} from './renderer'
import {ParticleInfo} from "../particles";

export type Dimensions = {
  width: number, height: number
}

export class GpuRenderer implements Renderer {
  public canvas: HTMLCanvasElement
  public debugStabilization = false
  public visualizeAge = false

  public drawGrid = false

  public backgroundColor: string = defaultBackgroundColor


  private pointShader: WebGLShader | undefined
  private meshShader: WebGLShader | undefined

  private pointVertexBuffer: WebGLBuffer | null = null
  private pointColorBuffer: WebGLBuffer | null = null
  private pointRadiusBuffer: WebGLBuffer | null = null

  private gridVertBuffer: WebGLBuffer | null = null
  private gridColorBuffer: WebGLBuffer | null = null


  private gl: WebGLRenderingContext
  private pointAliveBuffer: WebGLBuffer | null = null

  constructor() {
    this.canvas = this.setupCanvas('canvas-container')
    this.gl = this.canvas.getContext('webgl')!
    this.prepareShaders().then((result) => {
      this.pointShader = result.pointShader
      this.meshShader = result.meshShader
    })
  }

  public getCurrentDimensions(): Dimensions {
    return {width: this.canvas.width, height: this.canvas.height}
  }

  public draw(
    {
      particleCount,
      posX,
      posY,
      radii,
      colors,
      alive,
      stabilized
    }: ParticleInfo,
    grid: Grid,
    timestamp: number) {
    const {gl, pointShader} = this
    if (!(gl && pointShader)) return

    const particleColor = new Float32Array(particleCount * 3)
    const particlePos = new Float32Array(particleCount * 2)
    const particleAlive = new Float32Array(particleCount)

    for (let i = 0, k = 0; i < particleCount; i++, k += 2) {
      let finalColor: RGBColor = getColor(colors, i).map(x => x / 255) as RGBColor
      // DEBUG
      if (this.debugStabilization) {
        finalColor = stabilized[i] ? [0, 1, 0] :[1, 0, 0]
      }


      setColor(particleColor, i, finalColor)
      particleAlive[i] = alive[i] ? 1 : 0
      particlePos[k] = posX[i]
      particlePos[k + 1] = this.canvas.height - posY[i]
    }

    const backgroundColor = hex2dec(this.backgroundColor).map(value => value / 255)
    gl.clearColor(backgroundColor[0], backgroundColor[1], backgroundColor[2], 1.0)
    gl.clear(gl.COLOR_BUFFER_BIT)

    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height)

    if (this.drawGrid) {
      this.doDrawGrid(grid)
    }

    gl.clear(gl.DEPTH_BUFFER_BIT)


    gl.useProgram(pointShader)
    gl.uniform2f(gl.getUniformLocation(pointShader, 'domainSize'), this.canvas.width, this.canvas.height)

    if (this.pointVertexBuffer == null)
      this.pointVertexBuffer = gl.createBuffer()
    if (this.pointColorBuffer == null)
      this.pointColorBuffer = gl.createBuffer()
    if (this.pointRadiusBuffer == null)
      this.pointRadiusBuffer = gl.createBuffer()
    if (this.pointAliveBuffer == null)
      this.pointAliveBuffer = gl.createBuffer()

    gl.bindBuffer(gl.ARRAY_BUFFER, this.pointVertexBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, particlePos, gl.DYNAMIC_DRAW)
    const posLoc = gl.getAttribLocation(pointShader, 'attrPosition')
    gl.enableVertexAttribArray(posLoc)
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0)


    gl.bindBuffer(gl.ARRAY_BUFFER, this.pointRadiusBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, radii, gl.DYNAMIC_DRAW)
    const radiusLoc = gl.getAttribLocation(pointShader, 'attrRadius')
    gl.enableVertexAttribArray(radiusLoc)
    gl.vertexAttribPointer(radiusLoc, 1, gl.FLOAT, false, 0, 0)


    gl.bindBuffer(gl.ARRAY_BUFFER, this.pointColorBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, particleColor, gl.DYNAMIC_DRAW)
    const colorLoc = gl.getAttribLocation(pointShader, 'attrColor')
    gl.enableVertexAttribArray(colorLoc)
    gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0)


    gl.bindBuffer(gl.ARRAY_BUFFER, this.pointAliveBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, particleAlive, gl.DYNAMIC_DRAW)
    const aliveLoc = gl.getAttribLocation(pointShader, 'attrAlive')
    gl.enableVertexAttribArray(aliveLoc)
    gl.vertexAttribPointer(aliveLoc, 1, gl.FLOAT, false, 0, 0)

    gl.drawArrays(gl.POINTS, 0, particleCount)

    gl.disableVertexAttribArray(posLoc)
    gl.disableVertexAttribArray(colorLoc)
    gl.disableVertexAttribArray(radiusLoc)
    gl.disableVertexAttribArray(aliveLoc)

    gl.bindBuffer(gl.ARRAY_BUFFER, null)


  }


  private createShader(vsSource: string, fsSource: string) {
    const {gl} = this

    const vsShader = gl.createShader(gl.VERTEX_SHADER)
    if (!vsShader) throw new Error()
    gl.shaderSource(vsShader, vsSource)
    gl.compileShader(vsShader)
    if (!gl.getShaderParameter(vsShader, gl.COMPILE_STATUS))
      console.log('vertex shader compile error: ' + gl.getShaderInfoLog(vsShader))

    const fsShader = gl.createShader(gl.FRAGMENT_SHADER)
    if (!fsShader) throw new Error()
    gl.shaderSource(fsShader, fsSource)
    gl.compileShader(fsShader)
    if (!gl.getShaderParameter(fsShader, gl.COMPILE_STATUS))
      console.log('fragment shader compile error: ' + gl.getShaderInfoLog(fsShader))

    const shader = gl.createProgram()
    gl.attachShader(shader, vsShader)
    gl.attachShader(shader, fsShader)
    gl.linkProgram(shader)

    return shader
  }


  private setupCanvas(containerId: string) {
    const canvasContainer = document.getElementById(containerId) as HTMLDivElement
    const canvas = document.createElement('canvas')

    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    window.addEventListener('resize', (e) => {
      canvas.width = (e.target as Window).innerWidth
      canvas.height = (e.target as Window).innerHeight
    })

    canvasContainer.appendChild(canvas)

    return canvas

  }

  private async prepareShaders() {
    const [pointVertexShader, pointFragmentShader, meshVertexShader, meshFragmentShader] = await Promise.all([
      fetch('/shaders/point-vertex.glsl').then(response => response.text()),
      fetch('/shaders/point-fragment.glsl').then(response => response.text()),
      fetch('/shaders/mesh-vertex.glsl').then(response => response.text()),
      fetch('/shaders/mesh-fragment.glsl').then(response => response.text())
    ])


    return {
      pointShader: this.createShader(pointVertexShader, pointFragmentShader),
      meshShader: this.createShader(meshVertexShader, meshFragmentShader)
    }
  }

  private doDrawGrid(grid: Grid) {
    const {gl} = this
    if (!this.pointShader) return
    if (this.gridVertBuffer == null) {
      this.gridVertBuffer = gl.createBuffer()
      const cellCenters = new Float32Array(2 * grid.particlesPerCell.length)
      let p = 0


      for (let i = 0; i < grid.cellsX; i++) {
        for (let j = 0; j < grid.cellsY; j++) {
          cellCenters[p++] = (i + 0.5) * grid.cellSize
          cellCenters[p++] = (j + 0.5) * grid.cellSize
        }
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, this.gridVertBuffer)
      gl.bufferData(gl.ARRAY_BUFFER, cellCenters, gl.DYNAMIC_DRAW)
      gl.bindBuffer(gl.ARRAY_BUFFER, null)
    }

    if (this.gridColorBuffer == null)
      this.gridColorBuffer = gl.createBuffer()

    if (this.drawGrid) {

      const pointSize = 1   // ??

      gl.useProgram(this.pointShader)
      gl.uniform2f(gl.getUniformLocation(this.pointShader, 'domainSize'), this.canvas.width, this.canvas.height)
      gl.uniform1f(gl.getUniformLocation(this.pointShader, 'pointSize'), pointSize)
      gl.uniform1f(gl.getUniformLocation(this.pointShader, 'drawDisk'), 0.0)

      gl.bindBuffer(gl.ARRAY_BUFFER, this.gridVertBuffer)
      const posLoc = gl.getAttribLocation(this.pointShader, 'attrPosition')
      gl.enableVertexAttribArray(posLoc)
      gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0)

      gl.bindBuffer(gl.ARRAY_BUFFER, this.gridColorBuffer)
      gl.bufferData(gl.ARRAY_BUFFER, 255, gl.DYNAMIC_DRAW)

      const colorLoc = gl.getAttribLocation(this.pointShader, 'attrColor')
      gl.enableVertexAttribArray(colorLoc)
      gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0)


      gl.disableVertexAttribArray(posLoc)
      gl.disableVertexAttribArray(colorLoc)

      gl.bindBuffer(gl.ARRAY_BUFFER, null)
    }
  }
}